import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { BAT_SPRITE_DATA_URL } from './batSpriteAsset';

type Keyframes = ReturnType<typeof keyframes>;

/**
 * Halloween ambient effect: bat silhouettes that occasionally cross the viewport. Sibling
 * of SnowRain — same fixed, pointer-transparent overlay, same `paused` contract driven by
 * the idle manager in App.tsx.
 *
 * The bat is the designer's 8-frame sprite sheet (batSpriteAsset) and the flight is an
 * approximation of their Spine `batLeft` / `batRight` animations: a bat enters at one
 * edge, crosses in a few seconds while tilting and bobbing, shrinks as if receding into
 * the distance and fades out over the last stretch. Each bat is on screen for a small
 * fraction of its cycle and parked off-screen for the rest, so a sighting is an event
 * rather than a constant swarm. Variety comes from three flight patterns, two directions
 * (mirrored flights), a pair that flies together, and per-bat size, speed and wing-beat.
 * Cost: `batCount` tracks × 4 nodes, CSS keyframes only, no JS loop. Every animated property
 * is `transform` / `opacity`, so flight and wing-beat both run on the compositor and keep
 * going through main-thread hitches (navigation, search filters, modals, fast scroll).
 */

type FlightPattern = 'glide' | 'swoop' | 'drift';

interface BatTrackProps {
  $top: number;
  $duration: number;
  $delay: number;
  $reverse: boolean;
  $paused: boolean;
}

interface BatBodyProps {
  $width: number;
  $height: number;
  $pattern: FlightPattern;
  $duration: number;
  $delay: number;
  $paused: boolean;
}

/** Native frame size in the sprite sheet (px). */
const FRAME_W = 109;
const FRAME_H = 132;
/** Horizontal distance between frames (109px frame + 2px padding) and the sheet's edge offset. */
const FRAME_STRIDE = 111;
const FRAME_OFFSET = 2;
/** Whole sheet (890x136): 8 frames at the stride plus the edge padding on both sides. */
const SHEET_W = FRAME_OFFSET + FRAME_STRIDE * 8;
const SHEET_H = FRAME_OFFSET + FRAME_H + FRAME_OFFSET;

/**
 * Starting width range (px) before the desktop factor; the pattern's scale(0.28) end
 * brings it to ~14–19px (Spine: ~45–70px → ~15px on a 1440px scene). Desktop viewports
 * get 1.3× (64–90px) so the bats don't read as specks on a large screen.
 */
const BASE_WIDTH_MIN = 48;
const BASE_WIDTH_RANGE = 20;
const DESKTOP_MIN_WIDTH = 1024;
const DESKTOP_SIZE_FACTOR = 1.3;

/**
 * Crossing takes the first 18% of the cycle (3.9–8.3s on the default 22–46s cycles —
 * Spine's bats cross in ~3.9s); the bat waits off-screen for the rest. The start is
 * pushed out by more than the widest body (90px) so no bat is partly on screen at 0%.
 */
const CROSSING = 18;
const OFFSCREEN_START = 'calc(-10vw - 100px)';

const flyAcross = keyframes`
  0% { transform: translateX(${OFFSCREEN_START}); }
  ${CROSSING}%, 100% { transform: translateX(110vw); }
`;

// Mirror image of flyAcross: scaleX(-1) on the track also flips the body's tilt, so a
// reverse bat flies the true mirror of its pattern (Spine's batRight vs batLeft).
const flyAcrossReverse = keyframes`
  0% { transform: translateX(110vw) scaleX(-1); }
  ${CROSSING}%, 100% { transform: translateX(${OFFSCREEN_START}) scaleX(-1); }
`;

/*
 * Flight patterns, keyed to the same crossing window as the track. Spine values on a
 * 1440px scene: rotation swings between about -35° and +44° over 2–3 keys, vertical
 * drift of ±80–150px, bone scale falling to ~0.28 of its start (mostly in the second half)
 * and alpha → 0 from ~60% of the flight to the end.
 */
const glide = keyframes`
  0% { transform: translateY(0) rotate(-12deg) scale(1); opacity: 1; }
  7% { transform: translateY(-70px) rotate(26deg) scale(0.92); }
  11% { transform: translateY(-30px) rotate(-20deg) scale(0.66); opacity: 1; }
  ${CROSSING}%, 100% { transform: translateY(-90px) rotate(10deg) scale(0.28); opacity: 0; }
`;

const swoop = keyframes`
  0% { transform: translateY(-40px) rotate(-30deg) scale(1); opacity: 1; }
  6% { transform: translateY(80px) rotate(40deg) scale(0.95); }
  11% { transform: translateY(20px) rotate(-18deg) scale(0.7); opacity: 1; }
  ${CROSSING}%, 100% { transform: translateY(60px) rotate(12deg) scale(0.28); opacity: 0; }
`;

const drift = keyframes`
  0% { transform: translateY(20px) rotate(18deg) scale(1); opacity: 1; }
  8% { transform: translateY(-60px) rotate(-34deg) scale(0.9); }
  11% { transform: translateY(-20px) rotate(24deg) scale(0.62); opacity: 1; }
  ${CROSSING}%, 100% { transform: translateY(-110px) rotate(-8deg) scale(0.28); opacity: 0; }
`;

/*
 * Wing beat: one sprite frame per step. The whole sheet is a strip translated under a
 * frame-sized window (`transform`, so the beat stays on the compositor — animating
 * `background-position` is a main-thread style+paint animation and froze the wings on every
 * hitch). The 2px sheet padding means `steps(8)` over a uniform stride would drift off the
 * frames, so every frame's offset is spelled out and the timing function holds each value
 * until the next key (no interpolation). translate(-(2 + 111i), -2) lands exactly the pixels
 * that background-position: -(2 + 111i)px -2px did.
 */
const frameOffset = (i: number): string => `translate(${-(FRAME_OFFSET + FRAME_STRIDE * i)}px, ${-FRAME_OFFSET}px)`;

const flap = keyframes`
  0% { transform: ${frameOffset(0)}; }
  12.5% { transform: ${frameOffset(1)}; }
  25% { transform: ${frameOffset(2)}; }
  37.5% { transform: ${frameOffset(3)}; }
  50% { transform: ${frameOffset(4)}; }
  62.5% { transform: ${frameOffset(5)}; }
  75% { transform: ${frameOffset(6)}; }
  87.5% { transform: ${frameOffset(7)}; }
  100% { transform: ${frameOffset(0)}; }
`;

const PATTERN_KEYFRAMES: Record<FlightPattern, Keyframes> = { glide, swoop, drift };
const PATTERN_ORDER: FlightPattern[] = ['glide', 'swoop', 'drift', 'swoop', 'glide', 'drift'];

// The flock mounts a few seconds after the lobby (App.tsx staggers the seasonal effects) with
// the bats already mid-flight (negative delays), so the container fades in rather than popping.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const BatContainer = styled.div.attrs({
  className: 'bat-flyby-container',
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  overflow: hidden;
  z-index: 10000;
  animation: ${fadeIn} 0.8s ease-out 1;
`;

const BatTrack = styled.div<BatTrackProps>`
  position: absolute;
  top: ${(props) => props.$top}%;
  left: 0;
  transform: translateX(${(props) => (props.$reverse ? '110vw' : OFFSCREEN_START)}) translateZ(0);
  /* Keyframes chosen per bat go through css\`\` so styled-components injects them. */
  ${(props) => css`
    animation: ${props.$reverse ? flyAcrossReverse : flyAcross} ${props.$duration}s linear infinite;
  `}
  animation-delay: ${(props) => props.$delay}s;
  animation-play-state: ${(props) => (props.$paused ? 'paused' : 'running')};
`;

/* Sized to the bat's on-screen footprint; the sprite inside is scaled down to fill it. */
const BatBody = styled.div<BatBodyProps>`
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  ${(props) => css`
    animation: ${PATTERN_KEYFRAMES[props.$pattern]} ${props.$duration}s ease-in-out infinite;
  `}
  animation-delay: ${(props) => props.$delay}s;
  animation-play-state: ${(props) => (props.$paused ? 'paused' : 'running')};
`;

/* One native-size frame window of the sheet; the per-bat scale is inline, as before. */
const BatSprite = styled.div`
  width: ${FRAME_W}px;
  height: ${FRAME_H}px;
  overflow: hidden;
  transform-origin: 0 0;
`;

/*
 * The full sheet, slid under the frame window by the flap keyframes; the per-bat flap
 * duration and play state are inline styles so this stays a single static rule and the data
 * URL is injected once, not per bat.
 */
const BatStrip = styled.div`
  width: ${SHEET_W}px;
  height: ${SHEET_H}px;
  background-image: url(${BAT_SPRITE_DATA_URL});
  background-repeat: no-repeat;
  background-position: 0 0;
  transform: ${frameOffset(0)};
  animation-name: ${flap};
  animation-timing-function: step-end;
  animation-iteration-count: infinite;
`;

interface BatFlybyProps {
  batCount?: number;
  minDuration?: number;
  maxDuration?: number;
  paused?: boolean;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const BatFlyby: React.FC<BatFlybyProps> = ({ batCount = 6, minDuration = 22, maxDuration = 46, paused = false }) => {
  const reducedMotion = useMemo(prefersReducedMotion, []);

  const bats = useMemo(() => {
    // Sampled once with the rest of the random flock: a live resize across the desktop
    // threshold doesn't re-size the bats (they are generated once, like their lanes).
    const sizeFactor =
      typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH ? DESKTOP_SIZE_FACTOR : 1;

    return Array.from({ length: batCount }, (_, i) => {
      const pattern = PATTERN_ORDER[i % PATTERN_ORDER.length] ?? 'glide';
      const duration = minDuration + Math.random() * (maxDuration - minDuration);
      // Negative delay starts each bat somewhere inside its cycle, so the first sighting
      // doesn't wait a whole cycle and the bats are spread out from the first second.
      const delay = -Math.random() * duration;
      const width = (BASE_WIDTH_MIN + Math.random() * BASE_WIDTH_RANGE) * sizeFactor;

      return {
        id: i,
        pattern,
        // Spine spreads its bats across the upper 60% of the scene.
        top: 5 + Math.random() * 50,
        width,
        duration,
        delay,
        reverse: i % 2 === 1,
        // Spine steps one frame per 1/30s → a full 8-frame beat in ~0.27s.
        flapDuration: 0.24 + Math.random() * 0.08,
      };
    })
      .map((bat, i, all) => {
        // The second bat tails the first: same lane and direction, a beat behind.
        const leader = all[0];
        if (i === 0 || !leader) return bat;
        if (i !== 1) {
          // An odd flock leaves the last bat without a partner: it flies against the leader,
          // so a small flock still gets a right-to-left flight.
          const isSolo = all.length % 2 === 1 && i === all.length - 1;
          return isSolo ? { ...bat, reverse: !leader.reverse } : bat;
        }
        return {
          ...bat,
          top: leader.top + 4,
          reverse: leader.reverse,
          duration: leader.duration,
          delay: leader.delay + 0.7,
          width: leader.width * 0.8,
        };
      })
      .map((bat) => {
        const scale = bat.width / FRAME_W;
        return {
          ...bat,
          width: Math.round(FRAME_W * scale),
          height: Math.round(FRAME_H * scale),
          scale,
        };
      });
  }, [batCount, minDuration, maxDuration]);

  if (reducedMotion) {
    return null;
  }

  return (
    <BatContainer>
      {bats.map((bat) => (
        <BatTrack
          key={bat.id}
          $top={bat.top}
          $duration={bat.duration}
          $delay={bat.delay}
          $reverse={bat.reverse}
          $paused={paused}
        >
          <BatBody
            $width={bat.width}
            $height={bat.height}
            $pattern={bat.pattern}
            $duration={bat.duration}
            $delay={bat.delay}
            $paused={paused}
          >
            <BatSprite aria-hidden="true" style={{ transform: `scale(${bat.scale})` }}>
              <BatStrip
                style={{
                  animationDuration: `${bat.flapDuration}s`,
                  animationPlayState: paused ? 'paused' : 'running',
                }}
              />
            </BatSprite>
          </BatBody>
        </BatTrack>
      ))}
    </BatContainer>
  );
};

export default BatFlyby;
